import { API_FILTERS } from 'constants/api-v2'
import { useI18n } from 'context/I18nProvider'
import { useQuery } from 'context/QueryProvider'
import React, { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface FilterOption {
  value: string | number
  label: string
}

export type Value = FilterOption | string | number | boolean | null | undefined
export interface FilterValues {
  [key: string]: Value | Value[]
  q?: string
}

const cleanFilterValues = (values: FilterValues) => {
  const cleanedValues: FilterValues = {}

  Object.entries(values).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length === 0) return // We ignore empty arrays
    if (value === undefined || value === null) return // We ignore undefined/null

    cleanedValues[key] = value
  })

  return cleanedValues
}

const getValue = (value: Value | null | undefined): string => {
  if (value === null || value === undefined) {
    return ''
  }

  if (typeof value === 'object' && 'value' in value) {
    return String(value.value ?? '')
  }

  return value.toString()
}

const updateSearchParams = (searchParams: URLSearchParams, values: FilterValues) => {
  Object.entries(values).forEach(([key, value]) => {
    searchParams.delete(key)
    searchParams.delete(`${key}[]`)

    if (Array.isArray(value)) {
      value.forEach(val => {
        if (val === undefined) return
        searchParams.append(`${key}[]`, getValue(val).toString())
      })
      // Skip if it has no value (multi-button can have "true", "false" or null)
      // Loosely check for null or undefined, we want 0|false to be included
    } else if (value != null) searchParams.set(key, getValue(value).toString())
  })
}

const useFilters = (
  prefix: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updateHistory: boolean = true, // Kept for backward compatibility
  initialValues: FilterValues = {},
  callback: Function = () => {},
) => {
  const { client } = useQuery()
  const { i18n, i18name } = useI18n()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isInitialized, setInitialized] = React.useState(false)
  const [values, setValues] = React.useState<{ [prefix: string]: FilterValues }>({
    [prefix]: initialValues,
  })

  const fetchLabels = React.useCallback(
    async (newSearchParams?: URLSearchParams) => {
      async function init() {
        // For tests
        if (!client) return

        const queryParams = newSearchParams || searchParams
        const updatedValues: FilterValues = {}

        const response = await client.get(`${API_FILTERS}?${queryParams.toString()}`)

        queryParams.forEach((value: string, keyString: string) => {
          const key = keyString.replace('[]', '')

          if (key in response) {
            updatedValues[key] = response[key].map((option: any) => ({
              label: option.name ?? i18name(option) ?? option.fullName ?? option.code,
              value: option.id,
            }))
            // if key contains status
          } else if (!key.startsWith('order[') && key.toLowerCase().indexOf('status') !== -1) {
            // Special case for 'status' filters
            const previousStatuses = Array.isArray(updatedValues[key]) ? updatedValues[key] : []
            updatedValues[key] = [
              ...previousStatuses,
              {
                label: i18n(value),
                value,
              },
            ]
          } else {
            // All "hardcoded" multi-buttons filters
            const existing = updatedValues[key]
            if (Array.isArray(existing)) {
              updatedValues[key] = [...existing, value]
            } else if (existing != null) {
              updatedValues[key] = [existing as Value, value]
            } else {
              updatedValues[key] = value
            }
          }
        })
        setValues(prev => ({ ...prev, [prefix]: cleanFilterValues({ ...(prev?.[prefix] ?? {}), ...updatedValues }) }))
      }

      init()

      return () => {}
    },
    [client, searchParams, i18n, i18name, prefix],
  )

  const valuesToURLSearchParams = useCallback((vals?: FilterValues) => {
    const newParams = new URLSearchParams()

    updateSearchParams(newParams, vals ?? {})

    return newParams
  }, [])

  // Init only (guarded by isInitialized below). When updateHistory is false and initialValues
  // carries actual filter values (e.g. an automation's saved filters, not backed by the browser
  // URL), resolve labels from initialValues instead of the (irrelevant) router searchParams,
  // otherwise select fields would only show raw ids instead of their labels. Otherwise (no
  // initialValues), fall back to the router searchParams so pages that hydrate their initial
  // filters from a URL param (e.g. ?q=) keep working even when they don't sync back to it.
  const hasInitialValues = Object.keys(initialValues).length > 0
  React.useEffect(() => {
    if (!isInitialized) {
      fetchLabels(!updateHistory && hasInitialValues ? valuesToURLSearchParams(initialValues) : undefined)
      setInitialized(true)
    }

    return () => {}
  }, [fetchLabels, isInitialized, updateHistory, valuesToURLSearchParams, initialValues, hasInitialValues])

  const updateValue = React.useCallback(
    (key: string | FilterValues, value?: Value | Value[]) => {
      if (typeof key === 'string') {
        setValues(prev => {
          const currentValues = prev?.[prefix] ?? {}
          const updatedValues = { ...currentValues, [key]: value }
          if (key !== 'page' && 'page' in currentValues) {
            updatedValues.page = 1
          }
          return { ...prev, [prefix]: cleanFilterValues(updatedValues) }
        })
      } else {
        setValues(prev => {
          const currentValues = prev?.[prefix] ?? {}
          const updatedValues = { ...currentValues, ...key }
          if (!Object.keys(key).includes('page') && 'page' in currentValues) {
            updatedValues.page = 1
          }
          return { ...prev, [prefix]: cleanFilterValues(updatedValues) }
        })
      }

      callback?.()
    },
    [prefix, callback],
  )

  React.useEffect(() => {
    if (updateHistory) {
      const newParams = valuesToURLSearchParams(values?.[prefix])
      setSearchParams(newParams)
    }
  }, [prefix, values, setSearchParams, updateHistory, valuesToURLSearchParams])

  const reset = React.useCallback(
    (filters?: FilterValues) => {
      const newValues = filters ?? initialValues

      setValues({ [prefix]: cleanFilterValues(newValues) })

      // Update search params
      const newParams = valuesToURLSearchParams(cleanFilterValues(newValues))
      setSearchParams(newParams)

      // Explicitly call fetchLabels only when reset is called
      // Pass the new params directly to ensure it has the latest values
      fetchLabels(newParams)

      callback()
    },
    [initialValues, prefix, callback, valuesToURLSearchParams, setSearchParams, fetchLabels],
  )

  const getDependencyUrl = React.useCallback(
    (dependencyKey: string, outputKey: string) => {
      const dependentValues = values?.[prefix][dependencyKey] ?? []
      const params = new URLSearchParams()

      // optimize with updateSearchParams
      if (Array.isArray(dependentValues)) {
        dependentValues.forEach(val => {
          params.append(`${outputKey}[]`, getValue(val).toString())
        })
      } else {
        params.set(outputKey, getValue(dependentValues).toString())
      }

      return params.toString()
    },
    [values, prefix],
  )

  const url = React.useMemo(() => {
    const params = new URLSearchParams()

    updateSearchParams(params, values?.[prefix] ?? {})

    return params.toString()
  }, [values, prefix])

  const getCount = React.useCallback(
    (keys: string[]) =>
      keys.reduce((acc, key) => {
        const item = values?.[prefix][key]

        if (Array.isArray(item)) {
          return acc + item.length
        }

        return acc + ((item && Number(key in (values?.[prefix] ?? {}))) || 0)
      }, 0),
    [prefix, values],
  )

  return {
    values: values?.[prefix] ?? {},
    setValue: updateValue,
    url,
    reset,
    getDependencyUrl,
    getCount,
    isInitialized,
  }
}

export default useFilters
